/** Display labels and external hrefs for stored plain-text social / website contact values. */

export function normalizeSocialText(value: string | null | undefined): string | null {
  const trimmed = value?.trim()
  return trimmed || null
}

function stripUrlScheme(value: string): string {
  return value.replace(/^https?:\/\//i, '').replace(/^www\./i, '')
}

export function formatLinkedInDisplay(value: string): string {
  const trimmed = value.trim()
  const bare = stripUrlScheme(trimmed)
  const lower = bare.toLowerCase()
  if (lower.startsWith('linkedin.com/')) {
    const path = bare.slice('linkedin.com/'.length).replace(/\/$/, '')
    return path || bare
  }
  return trimmed.length > 36 ? `${trimmed.slice(0, 33)}…` : trimmed
}

export function buildLinkedInHref(value: string): string {
  const trimmed = value.trim()
  if (/^https?:\/\//i.test(trimmed)) return trimmed
  const bare = stripUrlScheme(trimmed)
  if (bare.toLowerCase().includes('linkedin.com')) {
    return `https://${bare.replace(/\/$/, '')}`
  }
  const handle = bare.replace(/^@/, '').replace(/^\//, '')
  return `https://linkedin.com/in/${handle}`
}

export function formatXDisplay(value: string): string {
  const trimmed = value.trim()
  const bare = stripUrlScheme(trimmed)
  const match = bare.match(/^(?:x\.com|twitter\.com)\/(@?[\w_]+)/i)
  if (match) {
    const handle = match[1].replace(/^@/, '')
    return `@${handle}`
  }
  if (trimmed.startsWith('@')) return trimmed
  const handle = trimmed.replace(/^@/, '')
  return `@${handle}`
}

export function buildXHref(value: string): string {
  const trimmed = value.trim()
  if (/^https?:\/\//i.test(trimmed)) return trimmed
  const bare = stripUrlScheme(trimmed)
  if (/^(x\.com|twitter\.com)\//i.test(bare)) {
    return `https://${bare.replace(/\/$/, '')}`
  }
  const handle = trimmed.replace(/^@/, '').replace(/^\//, '')
  return `https://x.com/${handle}`
}

export function formatWebsiteDisplay(value: string): string {
  return stripUrlScheme(value.trim()).replace(/\/$/, '')
}

export function buildWebsiteHref(value: string): string {
  const trimmed = value.trim()
  if (/^https?:\/\//i.test(trimmed)) return trimmed
  return `https://${trimmed.replace(/^\/\//, '')}`
}

function asHandle(value: string): string {
  return stripUrlScheme(value).replace(/^@/, '').replace(/\/$/, '')
}

function truncateDisplay(value: string, max = 36): string {
  return value.length > max ? `${value.slice(0, max - 3)}…` : value
}

function hostPathHref(value: string, host: string, pathPrefix: string): string {
  const trimmed = value.trim()
  if (/^https?:\/\//i.test(trimmed)) return trimmed
  const bare = stripUrlScheme(trimmed)
  if (bare.toLowerCase().includes(host)) {
    return `https://${bare.replace(/\/$/, '')}`
  }
  return `https://${host}/${pathPrefix}${asHandle(trimmed)}`
}

export function formatHandleDisplay(value: string): string {
  const trimmed = value.trim()
  const bare = stripUrlScheme(trimmed)
  if (bare.includes('/')) {
    const last = bare.replace(/\/$/, '').split('/').pop() ?? bare
    return last.startsWith('@') ? last : `@${last.replace(/^@/, '')}`
  }
  if (trimmed.startsWith('@')) return trimmed
  return `@${asHandle(trimmed)}`
}

export function buildInstagramHref(value: string): string {
  return hostPathHref(value, 'instagram.com', '')
}

export function buildFacebookHref(value: string): string {
  return hostPathHref(value, 'facebook.com', '')
}

export function buildThreadsHref(value: string): string {
  const trimmed = value.trim()
  if (/^https?:\/\//i.test(trimmed)) return trimmed
  const bare = stripUrlScheme(trimmed)
  if (/^(www\.)?threads\.net\//i.test(bare)) {
    return `https://${bare.replace(/\/$/, '')}`
  }
  const handle = asHandle(trimmed)
  return `https://www.threads.net/@${handle}`
}

export function formatThreadsDisplay(value: string): string {
  return formatHandleDisplay(value)
}

export function buildTikTokHref(value: string): string {
  const trimmed = value.trim()
  if (/^https?:\/\//i.test(trimmed)) return trimmed
  const bare = stripUrlScheme(trimmed)
  if (/^(www\.)?tiktok\.com\//i.test(bare)) {
    return `https://${bare.replace(/\/$/, '')}`
  }
  const handle = asHandle(trimmed)
  return `https://tiktok.com/@${handle}`
}

export function buildYouTubeHref(value: string): string {
  const trimmed = value.trim()
  if (/^https?:\/\//i.test(trimmed)) return trimmed
  const bare = stripUrlScheme(trimmed)
  if (/youtube\.com|youtu\.be/i.test(bare)) {
    return `https://${bare.replace(/\/$/, '')}`
  }
  return `https://youtube.com/@${asHandle(trimmed)}`
}

export function buildSubstackHref(value: string): string {
  const trimmed = value.trim()
  if (/^https?:\/\//i.test(trimmed)) return trimmed
  const bare = stripUrlScheme(trimmed)
  if (/substack\.com/i.test(bare)) {
    return `https://${bare.replace(/\/$/, '')}`
  }
  const handle = asHandle(trimmed)
  return `https://${handle}.substack.com`
}

export function buildGitHubHref(value: string): string {
  return hostPathHref(value, 'github.com', '')
}

export function formatSubstackDisplay(value: string): string {
  const trimmed = value.trim()
  const bare = stripUrlScheme(trimmed).replace(/\/$/, '')
  if (/substack\.com/i.test(bare)) return truncateDisplay(bare)
  return asHandle(trimmed)
}

export function formatGitHubDisplay(value: string): string {
  const trimmed = value.trim()
  const bare = stripUrlScheme(trimmed)
  if (/github\.com\//i.test(bare)) {
    return asHandle(bare.replace(/^github\.com\//i, ''))
  }
  return asHandle(trimmed)
}

export function formatInstagramDisplay(value: string): string {
  return formatHandleDisplay(value)
}

export function formatFacebookDisplay(value: string): string {
  const trimmed = value.trim()
  const bare = stripUrlScheme(trimmed)
  if (/facebook\.com|fb\.com/i.test(bare)) return truncateDisplay(bare.replace(/\/$/, ''))
  return asHandle(trimmed)
}

export function formatTikTokDisplay(value: string): string {
  return formatHandleDisplay(value)
}

export function formatYouTubeDisplay(value: string): string {
  return formatHandleDisplay(value)
}

export function buildMailtoHref(value: string): string {
  return `mailto:${value.trim()}`
}

/** Dial string for tel: links — strip spaces; keep + and digits. */
export function buildTelHref(value: string): string {
  const trimmed = value.trim()
  const dialable = trimmed.replace(/[^\d+]/g, '')
  return dialable ? `tel:${dialable}` : `tel:${trimmed}`
}
